import { apiSlice } from '@/store/api/apiSlice'

export type UserType = 'admin' | 'team_member'

export enum UserTypeId {
  Admin = 0,
  TeamMember = 1,
}

export type IdentityOrigin = 'invited' | 'sso' | 'self_registered' | 'imported'

/**
 * One user record: an account user, with the account it belongs to as part of its
 * identity rather than a nested list.
 */
export interface UserDetail {
  id: number
  email: string
  first_name: string
  last_name: string
  /** 'First Last', the one non-blank name, or the email. Never persisted server-side. */
  display_name: string
  title?: string | null
  phone?: string | null
  locale: string
  timezone: string
  identity_origin: IdentityOrigin
  /** Non-null: identity fields are owned by the organisation's IdP and read-only here. */
  sso_linked_at: string | null
  user_type: UserType
  is_active: boolean
  status: string
  keycloak_user_id: string | null
  account: {
    id: number
    display_name: string | null
    contact_email: string | null
    keycloak_realm_id: string | null
    keycloak_jwks_url: string | null
    is_active: boolean
  }
  application_users: Array<{
    application: {
      id: number
      name: string
    }
    application_user_id: string
  }>
}

export interface UpdateUserRequest {
  first_name?: string
  last_name?: string
  title?: string
  phone?: string
  email?: string
  user_type?: UserType
  is_active?: boolean
}

/** Exactly one account identifier is required; TypeScript rejects both/neither. */
type CreateUserAccountRef =
  | {
      /** GIM PlatformAccount ID. */
      account_id: number
      workflow_account_id?: never
      dam_account_id?: never
    }
  | {
      /** Workflow (Opus) AccountUID; the backend resolves it to the platform account. */
      workflow_account_id: string
      account_id?: never
      dam_account_id?: never
    }
  | {
      /** DAM tenant id; the backend resolves it to the platform account. */
      dam_account_id: string
      account_id?: never
      workflow_account_id?: never
    }

export type CreateUserRequest = CreateUserAccountRef & {
  user: {
    email: string
    first_name: string
    last_name: string
    title?: string
    phone?: string
    locale?: string
    timezone?: string
    user_type?: UserType
  }
}

/** The create endpoint returns the new account user without its account or application users. */
export type CreateUserResponse = Omit<
  UserDetail,
  'account' | 'application_users' | 'sso_linked_at'
>

export const userApi = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getCurrentUser: builder.query<UserDetail, void>({
      query: () => `/public/v1/users/me/`,
      providesTags: result => [{ type: 'User', id: result?.id }],
    }),
    getUserDetails: builder.query<UserDetail, number | undefined>({
      query: id => `/public/v1/users/${id}/`,
      providesTags: (_result, _error, id) => [{ type: 'User', id }],
    }),
    updateUser: builder.mutation<
      UserDetail,
      { id: number; data: UpdateUserRequest }
    >({
      query: ({ id, data }) => ({
        url: `/public/v1/users/${id}/update/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'User', id }],
    }),
    createUser: builder.mutation<CreateUserResponse, CreateUserRequest>({
      query: data => ({
        url: '/public/v1/staff/users/create/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),
    createUserPublic: builder.mutation<CreateUserResponse, CreateUserRequest>({
      query: data => ({
        url: '/public/v1/users/create/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),
  }),
})

export const {
  useGetCurrentUserQuery,
  useGetUserDetailsQuery,
  useUpdateUserMutation,
  useCreateUserMutation,
  useCreateUserPublicMutation,
} = userApi

// Custom hook wrapper that handles skip logic
export const useUserDetails = (id: number | undefined) => {
  return useGetUserDetailsQuery(id, {
    skip: !id,
  })
}
